import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Container,
  Form,
  Alert,
  ProgressBar,
  Card,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { BsSun, BsMoon, BsArrowLeft, BsX } from "react-icons/bs";
import { useTheme } from "../../contexts/ThemeContext";
import { callOperation } from "../../utils/apiHelper.js";
import LoadingSpinner from "../common/LoadingSpinner";

const FileUploaderPage = ({ onFileSelected, token }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [currentActiveFileId, setCurrentActiveFileId] = useState(null);
  const [openingFile, setOpeningFile] = useState(false);
  const [uploadCompleted, setUploadCompleted] = useState(0); // Trigger for refresh
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setErrorMessage("");

    if (selectedFile) {
      // Validate file type
      const validExtensions = [".mpegg"];
      const fileExtension = selectedFile.name
        .toLowerCase()
        .substring(selectedFile.name.lastIndexOf("."));

      if (!validExtensions.includes(fileExtension)) {
        setErrorMessage(
          `Invalid file type. Please select a file with extension: ${validExtensions.join(
            ", "
          )}`
        );
        setFile(null);
        return;
      }

      // Validate file size (5GB limit)
      const maxSize = 5 * 1024 * 1024 * 1024; // 5GB in bytes
      if (selectedFile.size > maxSize) {
        setErrorMessage(
          "File size exceeds 5GB limit. Please select a smaller file."
        );
        setFile(null);
        return;
      }

      setFile(selectedFile);
    } else {
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setErrorMessage("Please select a file to upload.");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setErrorMessage("");

    try {
      // Step 1: Get presigned upload URL
      const uploadResponse = await callOperation(
        "generate_upload_url",
        { filename: file.name },
        token,
        "files"
      );

      if (!uploadResponse.data) {
        throw new Error("Failed to get upload URL");
      }

      const { upload_url, fields } = uploadResponse.data;

      // Step 2: Upload file using presigned URL
      const formData = new FormData();

      // Add presigned URL fields first
      Object.keys(fields).forEach((key) => {
        formData.append(key, fields[key]);
      });

      // Add the file last
      formData.append("file", file);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", upload_url, true);

      // Update progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });

      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 201 || xhr.status === 204) {
          setFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          // Trigger effect to refresh file list
          setUploadCompleted((prev) => prev + 1);
        } else {
          setErrorMessage(`Upload failed (${xhr.status})`);
        }
        setUploading(false);
      };

      xhr.onerror = () => {
        setErrorMessage("Network error during upload.");
        setUploading(false);
      };

      xhr.send(formData);
    } catch (error) {
      setErrorMessage(`Upload failed: ${error.message}`);
      setUploading(false);
    }
  };

  // Load available files
  const loadFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      // Get list of files
      const response = await callOperation("list_files", {}, token, "files");
      if (response.data && response.data.files) {
        let files = response.data.files;

        // Get current active file from sample info
        try {
          const sampleInfoResponse = await callOperation(
            "get_sample_info",
            {},
            token,
            "query"
          );
          if (sampleInfoResponse.data && sampleInfoResponse.data.current_file) {
            // Find the file ID that matches the current file name
            const activeFile = files.find(
              (file) => file.name === sampleInfoResponse.data.current_file
            );
            if (activeFile) {
              setCurrentActiveFileId(activeFile.id);

              // Sort files to put active file first
              files = [
                activeFile,
                ...files.filter((f) => f.id !== activeFile.id),
              ];
            }
          }
        } catch (error) {
          // If sample info fails, just continue without marking active file
          console.log("Could not get current file info");
        }

        setAvailableFiles(files);
      }
    } catch (error) {
      console.error("Failed to load files:", error);
    } finally {
      setLoadingFiles(false);
    }
  }, [token]);

  // Open selected file in workspace
  const handleFileSelection = useCallback(
    async (fileId) => {
      setOpeningFile(true);
      setErrorMessage("");

      try {
        // Call switch_file operation to switch to selected file
        await callOperation("switch_file", { file_id: fileId }, token, "query");

        // Navigate back to main workspace
        onFileSelected();
      } catch (error) {
        setErrorMessage(`Failed to open file: ${error.message}`);
      } finally {
        setOpeningFile(false);
      }
    },
    [token, onFileSelected]
  );

  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }, []);

  const formatDate = useCallback((timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  }, []);

  // Delete file handler
  const handleFileDelete = useCallback(
    async (fileId, fileName) => {
      if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
        return;
      }

      setErrorMessage("");
      try {
        const response = await callOperation(
          "delete_file",
          { file_id: fileId },
          token,
          "files"
        );

        if (response.data && response.data.success) {
          // Refresh the file list after successful deletion
          loadFiles();
          // Clear selection if deleted file was selected
          if (selectedFileId === fileId) {
            setSelectedFileId(null);
          }
        } else {
          setErrorMessage(response.data?.error || "Failed to delete file");
        }
      } catch (error) {
        setErrorMessage(`Delete failed: ${error.message}`);
      }
    },
    [token, loadFiles, selectedFileId]
  );

  useEffect(() => {
    // Load available files on mount
    loadFiles();
  }, [loadFiles]);

  // Effect to refresh file list after upload completion
  useEffect(() => {
    if (uploadCompleted > 0) {
      // Add small delay to allow backend processing
      const timer = setTimeout(() => {
        loadFiles();
      }, 2000); // 2 second delay

      return () => clearTimeout(timer);
    }
  }, [uploadCompleted, loadFiles]);

  return (
    <Container
      className="file-uploader-page"
      style={{ minHeight: "100vh", padding: "2rem 1rem" }}
    >
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          {/* Header with logo and theme toggle */}
          <div className="text-center mb-4">
            <img
              src="/MPEG-G.png"
              alt="MPEG-G Logo"
              className="img-fluid mb-3"
              style={{ maxWidth: "250px" }}
            />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <button
                className="btn btn-sm btn-primary btn-animate d-flex align-items-center gap-1 justify-content-center"
                onClick={onFileSelected}
                type="button"
                title="Go to main page"
              >
                <BsArrowLeft />
                <span className="fw-medium d-none d-md-inline">Back</span>
              </button>
              <button
                className={`btn btn-sm rounded-pill btn-animate pulse-hover flex-shrink-0 ${
                  isDarkMode ? "btn-primary" : "btn-outline-secondary"
                }`}
                onClick={toggleTheme}
                aria-label="Toggle dark mode"
                title={
                  isDarkMode ? "Switch to light mode" : "Switch to dark mode"
                }
                style={{ minWidth: "40px" }}
              >
                {isDarkMode ? <BsSun size={18} /> : <BsMoon size={18} />}
              </button>
            </div>
          </div>

          {errorMessage && (
            <Alert variant="danger" className="mb-3">
              {errorMessage}
            </Alert>
          )}

          {/* File Upload Section */}
          <Card className="mb-4 upload-card">
            <Card.Header>
              <h6 className="mb-0">Add New File</h6>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Select MPEG-G file (.mpegg)</Form.Label>
                  <Form.Control
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    disabled={uploading}
                    accept=".mpegg"
                  />
                  <Form.Text className="text-muted">
                    Maximum file size: 5GB
                  </Form.Text>
                </Form.Group>

                {uploading && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small>Uploading...</small>
                      <small>{uploadProgress}%</small>
                    </div>
                    <ProgressBar now={uploadProgress} variant="primary" />
                  </div>
                )}

                <div className="d-grid">
                  <Button
                    variant="primary"
                    onClick={handleUpload}
                    disabled={uploading || !file}
                    className="d-flex align-items-center justify-content-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <LoadingSpinner size="1em" /> Uploading...
                      </>
                    ) : (
                      "Upload File"
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Available Files Section */}
          <Card className="file-list-card">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Select File</h6>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={loadFiles}
                disabled={loadingFiles}
                className="d-flex align-items-center justify-content-center"
              >
                {loadingFiles ? <LoadingSpinner size="1em" /> : "Refresh"}
              </Button>
            </Card.Header>
            <Card.Body>
              {loadingFiles ? (
                <div className="d-flex align-items-center justify-content-center py-3">
                  <LoadingSpinner size="1em" />
                  <span className="ms-2">Loading files...</span>
                </div>
              ) : availableFiles.length > 0 ? (
                <div className="file-list">
                  {availableFiles.map((file, index) => (
                    <div
                      key={index}
                      className={`file-item p-3 border rounded mb-2 ${
                        selectedFileId === file.id ? "selected" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedFileId(file.id)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {file.name}
                            {currentActiveFileId === file.id && (
                              <Badge bg="success" className="ms-2">
                                Active
                              </Badge>
                            )}
                          </h6>
                          <div className="file-metadata">
                            <Badge
                              bg={
                                file.type === "shared" ? "warning" : "primary"
                              }
                              className="me-2"
                            >
                              {file.type}
                            </Badge>
                            <small className="text-muted">
                              {formatFileSize(file.size)} • Modified:{" "}
                              {formatDate(file.modified)}
                            </small>
                          </div>
                          <div className="mt-1">
                            {file.filters && file.filters.length > 0 ? (
                              file.filters.map((filter, idx) => (
                                <Badge key={idx} className="me-1 filter-badge">
                                  {filter}
                                </Badge>
                              ))
                            ) : (
                              <Badge className="filter-badge">No filters</Badge>
                            )}
                          </div>
                        </div>
                        {selectedFileId === file.id &&
                          currentActiveFileId !== file.id && (
                            <div className="d-flex gap-2">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFileSelection(file.id);
                                }}
                                disabled={openingFile}
                                className="d-flex align-items-center justify-content-center file-action-btn"
                              >
                                {openingFile ? (
                                  <LoadingSpinner size="1em" />
                                ) : (
                                  "Open"
                                )}
                              </Button>
                              {file.type === "user" && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFileDelete(file.id, file.name);
                                  }}
                                  disabled={openingFile}
                                  className="d-flex align-items-center justify-content-center file-action-btn-icon"
                                  title="Delete file"
                                >
                                  <BsX size={20} />
                                </Button>
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-3 text-muted">
                  <p>No files available. Upload a file to get started.</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default FileUploaderPage;
